import { type Configuration, PublicClientApplication } from '@azure/msal-browser';

/**
 * MSAL configuration for Microsoft EntraID (Azure AD) authentication.
 *
 * Two distinct instances live in this app, and they must not be confused:
 *
 * 1. {@link msalInstance} below — built from build-time environment variables and provided at the root
 *    of the tree. It serves the FileGrant EntraID login only.
 * 2. The SharePointGrant instance, built at runtime by {@link createSharePointMsalInstance} from
 *    `GET /api/sharepoint/signin/config` and provided by `SharePointMsalProvider` over the viewer and
 *    the editor subtree only. Its client id and tenant belong to the CUSTOMER's app registration, are
 *    per company, and therefore cannot be baked into the bundle: one deployment serves many tenants,
 *    and an id from the wrong tenant produces a sign-in that can never satisfy the tenant check the
 *    API makes on the delegated token.
 *
 * Required environment variables:
 * - VITE_APP_ENTRAID_CLIENT_ID: The Azure AD Application (client) ID
 * - VITE_APP_ENTRAID_TENANT_ID: The Azure AD Tenant ID (optional, defaults to "common")
 * - VITE_APP_ENTRAID_REDIRECT_URI: The redirect URI after authentication (optional, defaults to current origin + /login)
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_APP_ENTRAID_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${
      import.meta.env.VITE_APP_ENTRAID_TENANT_ID || 'common'
    }`,
    redirectUri: import.meta.env.VITE_APP_ENTRAID_REDIRECT_URI || `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
  },
};

/**
 * Scopes required for authentication
 */
export const loginRequest = {
  scopes: ['User.Read'],
};

/**
 * Delegated Graph scopes used to read a file the user opens from SharePoint.
 *
 * Read-only on purpose: the viewer must never be able to write back, so no bug on that path can
 * corrupt an encrypted file. `Files.Read.All` requires admin consent on the customer tenant —
 * without it the sign-in prompt fails and the viewer surfaces an authentication error.
 *
 * `redirectUri` points at a static blank page instead of `/login`, and only for this request:
 * the popup and the hidden iframe must not boot a second copy of the app. That copy would call
 * `handleRedirectPromise` with its own (empty) sessionStorage, fail with
 * `no_token_request_cache_error` and swallow the response the calling window is polling for.
 * The interactive login of FileGrant keeps using the redirect URI configured above.
 */
export const sharePointGraphRequest = {
  redirectUri: `${window.location.origin}/msal-redirect.html`,
  scopes: ['Files.Read.All'],
};

/**
 * Delegated Graph scopes for OPENING AN EDITING SESSION on an encrypted SharePoint file.
 *
 * A separate request, asked for only when the user clicks Edit, so the viewer keeps its read-only
 * token. The write scope is not used to write anything from the browser: the API uses it to ask
 * SharePoint whether *this user* may modify *this file* (it creates an upload session and cancels it
 * immediately). The content itself is written back by the API with the company's app-only credential,
 * which cannot tell who was driving the editor — hence the question.
 *
 * `Files.ReadWrite.All` needs its own admin consent on the customer tenant. Without it the token
 * request fails, or the probe answers no: either way the document opens read-only, which is the right
 * outcome when nobody has granted write access. Requested incrementally (MSAL adds it to the existing
 * session) rather than at login, so a tenant that only wants viewing never sees the prompt.
 */
export const sharePointGraphWriteRequest = {
  redirectUri: `${window.location.origin}/msal-redirect.html`,
  scopes: ['Files.ReadWrite.All'],
};

/**
 * MSAL instance for the SharePointGrant viewer/editor, bound to ONE company's Entra ID tenant and to
 * its public (SPA) app registration.
 *
 * Deliberately separate from {@link msalInstance}: a `PublicClientApplication` is built around a single
 * client id and authority, both of which are per company here and only known after
 * `GET /api/sharepoint/signin/config` answers. Turning the whole app's bootstrap asynchronous to wait
 * for that call would be the wrong trade — the call needs an authenticated session, which the login
 * page by definition does not have.
 *
 * `initialize()` is the caller's job (see `SharePointMsalProvider`): acting on an uninitialised
 * instance throws `uninitialized_public_client_application`.
 *
 * `redirectUri` points at the static blank page, like {@link sharePointGraphRequest}: every token here
 * is acquired through a popup or a hidden iframe, and a second copy of the app booting in that window
 * would consume the response the calling window is polling for.
 */
export function createSharePointMsalInstance(config: {
  spaClientId: string;
  tenantId: string;
}): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      clientId: config.spaClientId,
      redirectUri: `${window.location.origin}/msal-redirect.html`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  });
}

/**
 * Create and export MSAL instance
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/*
 * Initialize MSAL early, but do NOT process the redirect response here: `MsalProvider` already
 * calls `handleRedirectPromise` once, and it skips the call inside an iframe. A second,
 * unconditional call at module scope also runs in the popup and iframe windows, where the
 * request cache is empty — it fails with `no_token_request_cache_error` and consumes the
 * response the calling window is waiting for.
 */
msalInstance.initialize().catch((error) => {
  console.error('MSAL initialization error:', error);
});
